import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail, Copy, Check, Send, Loader2, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const buildLetter = (residentName, landlordName) => {
  const greeting = landlordName ? `Dear ${landlordName},` : 'Dear Sir or Madam,';
  const signature = residentName || '[Your name]';
  return {
    subject: 'Would you consider sponsoring ResiAX for your residents?',
    body: `${greeting}

I'm one of your residents and I'd like to ask you to consider sponsoring ResiAX for the homes you manage.

ResiAX gives residents one place to report and track issues, keep a home diary and evidence log, and communicate clearly - and it gives landlords and managing agents a clearer, fairer way to receive reports, cut repeat chasing and evidence how issues are handled.

Provider plans start at \u00a349/month for up to 25 active homes, and ResiAX is currently offering a Founding Provider Pilot to its first five organisations. There's an overview at resiax.com/providers and full fee details at resiax.com/fees.

Sponsoring doesn't give access to residents' private records - it simply funds better communication between us.

Would you be open to taking a look?

Kind regards,
${signature}`,
  };
};

const SponsorReferralModal = ({ open, onOpenChange }) => {
  const [residentName, setResidentName] = useState('');
  const [landlordName, setLandlordName] = useState('');
  const [landlordEmail, setLandlordEmail] = useState('');
  const [residentEmail, setResidentEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const letter = useMemo(() => buildLetter(residentName.trim(), landlordName.trim()), [residentName, landlordName]);

  const mailtoHref = `mailto:${encodeURIComponent(landlordEmail.trim())}?subject=${encodeURIComponent(letter.subject)}&body=${encodeURIComponent(letter.body)}`;

  const copyLetter = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${letter.subject}\n\n${letter.body}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setError('Could not copy — please select the letter text and copy it manually.');
    }
  };

  const sendOnMyBehalf = async () => {
    setError('');
    if (!residentName.trim() || !residentEmail.trim()) {
      setError('Please add your name and email so we can keep you updated.');
      return;
    }
    if (!landlordName.trim()) {
      setError('Please add your landlord or managing agent\u2019s name.');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/waiting-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: residentName.trim(),
          email: residentEmail.trim(),
          user_type: 'resident',
          organisation: `Sponsor referral \u2192 ${landlordName.trim()}${landlordEmail.trim() ? ` <${landlordEmail.trim()}>` : ''}`,
          referral_provider: landlordName.trim(),
          referral_provider_email: landlordEmail.trim() || null,
          pain_points: 'Asked ResiAX to contact their landlord/managing agent about sponsorship.',
          interest_type: 'sponsorship',
          source_page: typeof window !== 'undefined' ? window.location.pathname : '/',
        }),
      });
      if (!res.ok) throw new Error('Request failed');
      setSent(true);
    } catch {
      setError('Something went wrong — please try again, or use the email/copy options above.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FFFAF8]" data-testid="sponsor-referral-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#1F1E1E]">Ask my landlord to sponsor ResiAX</DialogTitle>
          <DialogDescription className="text-sm text-[#3B3535]/70">
            We&apos;ll draft the letter — you choose how to send it. Only the minimum introduction information is
            shared; never complaints, health or other sensitive details without your explicit permission.
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="py-8 text-center" data-testid="sponsor-referral-success">
            <div className="w-14 h-14 rounded-full bg-[#B5541B]/10 flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-[#B5541B]" />
            </div>
            <h3 className="font-bold text-[#1F1E1E] mb-2">Thanks, {residentName.trim()}!</h3>
            <p className="text-sm text-[#3B3535]/75 max-w-sm mx-auto">
              We&apos;ll make a gentle introduction to {landlordName.trim()} about sponsoring ResiAX and keep you
              posted at {residentEmail.trim()}.
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="sr-resident-name" className="text-[#3B3535] font-medium">Your name *</Label>
                <Input
                  id="sr-resident-name"
                  data-testid="sponsor-resident-name"
                  value={residentName}
                  onChange={(e) => setResidentName(e.target.value)}
                  placeholder="e.g. Sarah Thompson"
                  className="bg-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sr-landlord-name" className="text-[#3B3535] font-medium">Landlord / managing agent name *</Label>
                <Input
                  id="sr-landlord-name"
                  data-testid="sponsor-landlord-name"
                  value={landlordName}
                  onChange={(e) => setLandlordName(e.target.value)}
                  placeholder="e.g. Acme Property Management"
                  className="bg-white"
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="sr-landlord-email" className="text-[#3B3535] font-medium">Their email <span className="text-[#3B3535]/50 font-normal">(optional — pre-fills the draft)</span></Label>
                <Input
                  id="sr-landlord-email"
                  data-testid="sponsor-landlord-email"
                  type="email"
                  value={landlordEmail}
                  onChange={(e) => setLandlordEmail(e.target.value)}
                  placeholder="e.g. lettings@acme.co.uk"
                  className="bg-white"
                />
              </div>
            </div>

            {/* Letter preview */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-[#3B3535] font-medium">Your letter</Label>
                <span className="text-xs text-[#3B3535]/50">updates as you type</span>
              </div>
              <div className="bg-white border border-[#B5541B]/15 rounded-xl p-4 text-sm text-[#3B3535] leading-relaxed whitespace-pre-wrap max-h-56 overflow-y-auto" data-testid="sponsor-letter-preview">
                <p className="font-semibold mb-2">Subject: {letter.subject}</p>
                {letter.body}
              </div>
            </div>

            {/* Send actions */}
            <div className="flex flex-wrap gap-3">
              <a
                href={mailtoHref}
                data-testid="sponsor-open-email"
                className="inline-flex items-center gap-2 bg-[#B5541B] hover:bg-[#8B3F14] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all"
              >
                <Mail className="w-4 h-4" />
                Open in my email app
              </a>
              <button
                onClick={copyLetter}
                data-testid="sponsor-copy-letter"
                className="inline-flex items-center gap-2 border border-[#B5541B]/30 text-[#B5541B] hover:bg-[#B5541B]/10 text-sm font-semibold px-5 py-2.5 rounded-full transition-all"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy letter'}
              </button>
            </div>

            {/* Or let ResiAX contact them */}
            <div className="border-t border-[#B5541B]/10 pt-5">
              <p className="text-sm font-semibold text-[#1F1E1E] mb-1">Prefer we reach out for you?</p>
              <p className="text-xs text-[#3B3535]/65 mb-3">
                Leave your email and we&apos;ll contact {landlordName.trim() || 'your landlord'} with a short,
                friendly introduction — and keep you updated.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  data-testid="sponsor-resident-email"
                  type="email"
                  value={residentEmail}
                  onChange={(e) => setResidentEmail(e.target.value)}
                  placeholder="Your email"
                  className="bg-white flex-1"
                />
                <button
                  onClick={sendOnMyBehalf}
                  disabled={sending}
                  data-testid="sponsor-send-on-behalf"
                  className="inline-flex items-center justify-center gap-2 bg-[#1F1E1E] hover:bg-[#2D2A2A] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all disabled:opacity-60"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Ask ResiAX to contact them
                </button>
              </div>
              {error && <p className="text-xs text-red-600 mt-2" data-testid="sponsor-referral-error">{error}</p>}
            </div>

            <p className="flex items-start gap-2 text-xs text-[#3B3535]/60">
              <ShieldCheck className="w-4 h-4 text-[#B5541B] shrink-0 mt-0.5" />
              Your account always belongs to you. Sponsorship never gives a sponsor access to your private records.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SponsorReferralModal;
